import type { CapacityLiveDto, FairUseRowDto, MailStatusDto } from '@dbrain/shared';

/**
 * Phase 16a (ADR-0023, 06 §28.5, B2cPlatformKapasite): what Platform › Kapasite's tiles, its e-mail card and its fair
 * use table say, from the live numbers.
 */

/** Who waits: nobody, only Ücretsiz, only Pro, both, or a queue the gateway did not split by plan */
export type QueueMix = 'none' | 'free' | 'pro' | 'mixed' | 'unsplit';

export function queueMix(queue: CapacityLiveDto['queue']): QueueMix {
  if (queue.total <= 0) return 'none';
  if (queue.free === queue.total) return 'free';
  if (queue.pro === queue.total) return 'pro';
  return queue.pro + queue.free > 0 ? 'mixed' : 'unsplit';
}

/** The longest wait in whole seconds, rounded up; 0 when nobody waits */
export const waitSeconds = (ms: number) => Math.max(0, Math.ceil(ms / 1000));

/** Today's tokens by plan as whole percents that add up to 100 ("Pro %71, Ücretsiz %29"); null before any */
export function tokenShares(tokens: CapacityLiveDto['tokensToday']): { pro: number; free: number } | null {
  const sum = tokens.pro + tokens.free;
  if (sum <= 0) return null;
  const pro = Math.round((tokens.pro / sum) * 100);
  return { pro, free: 100 - pro };
}

export const MAIL_ERROR_KINDS = ['auth', 'connection', 'timeout', 'tls', 'rejected', 'other'] as const;
export type MailErrorKind = (typeof MAIL_ERROR_KINDS)[number];

/** The e-mail card (D-272): nothing sent yet, working, or failing with the kind of its last error */
export function mailState(mail: MailStatusDto | null): { state: 'none' } | { state: 'working'; at: string } | { state: 'failing'; at: string; kind: MailErrorKind } {
  if (!mail?.lastAttemptAt) return { state: 'none' };
  if (mail.failing) {
    const kind = (MAIL_ERROR_KINDS as readonly string[]).includes(mail.lastErrorKind ?? '') ? (mail.lastErrorKind as MailErrorKind) : 'other';
    return { state: 'failing', at: mail.lastFailureAt ?? mail.lastAttemptAt, kind };
  }
  return mail.lastSuccessAt ? { state: 'working', at: mail.lastSuccessAt } : { state: 'none' };
}

/** A fair use row's share of its daily limit, 0–100 (the bar; amber from 80%) */
export function dailyShare(row: Pick<FairUseRowDto, 'today' | 'dailyTokens'>): number {
  return row.dailyTokens > 0 ? Math.min(100, Math.round((row.today / row.dailyTokens) * 100)) : 0;
}

/** The fair use mail's button: on a flagged row, unless one went out today already */
export function canMailFairUse(row: Pick<FairUseRowDto, 'flagged' | 'lastMailAt'>, todayKey: string, dayOf: (iso: string) => string): boolean {
  return row.flagged && (row.lastMailAt === null || dayOf(row.lastMailAt) !== todayKey);
}
